"use client";

import "swiper/css";
import "swiper/css/pagination";
import "./styles/BannerCarousel_1.css";

import { Box, IconButton } from "@chakra-ui/react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useMemo, useState } from "react";
import { BannerCarouselProps } from "../../../@types/Carousel";
import BannerCarouselItem from "./BannerCarouselItem";
import CenteredBannerCarouselItem from "./CenteredBannerCarouselItem";

const BannerVariants = {
  default: BannerCarouselItem,
  centered: CenteredBannerCarouselItem,
};

function renderPaginationBullet(index: string, className: string) {
  return `<span class="${className}"></span>`;
}

function BannerCarousel({
  swiperConfig,
  bannerItems,
  showPagination = false,
  showNavigation = false,
  bannerWrapperProps,
  variant = "default",
  iconButtonProps,
  ...props
}: BannerCarouselProps) {
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [activeRealIndex, setActiveRealIndex] = useState(0);
  const modules = [Autoplay, Pagination, Navigation].concat(
    swiperConfig?.modules || [],
  );

  const BannerRenderer = variant
    ? BannerVariants[variant as keyof typeof BannerVariants]
    : BannerCarouselItem;

  const paginationConfig = useMemo(() => {
    if (!showPagination) return false as const;
    return Object.assign(
      {
        enabled: true,
        clickable: true,
        bulletActiveClass: "banner-carousel-1__bullet-active",
        bulletClass: "banner-carousel-1__bullet",
        renderBullet: renderPaginationBullet,
      },
      swiperConfig?.pagination || {},
    );
  }, [showPagination, swiperConfig?.pagination]);

  const navigationConfig = useMemo(
    () =>
      Object.assign(
        {
          enabled: true,
          prevEl,
          nextEl,
        },
        swiperConfig?.navigation || {},
      ),
    [prevEl, nextEl, swiperConfig?.navigation],
  );

  const defaultIconButtonProps = {
    variant: "unstyled",
    fontSize: "1.1rem",
    color: "white",
    top: "50%",
    zIndex: 100,
    bg: "whiteAlpha.800",
    borderRadius: "4px",
    w: "32px",
    h: "32px",
    minW: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    sx: {
      transform: "translateY(-50%)",
      "& svg": { color: "gray.700" },
    },
    _hover: {
      bg: "white",
      sx: { "& svg": { color: "primary.500" } },
    },
    ...iconButtonProps,
  };

  return (
    <Box position="relative" zIndex={0} {...bannerWrapperProps}>
      <Swiper
        spaceBetween={50}
        slidesPerView={1}
        loop
        autoplay={{
          delay: 5000,
        }}
        {...swiperConfig}
        modules={modules}
        pagination={paginationConfig}
        navigation={navigationConfig}
        onSlideChange={(swiper) => setActiveRealIndex(swiper.realIndex)}
      >
        {bannerItems?.map((data, index) => (
          <SwiperSlide key={`banner_slide_${index}`}>
            <BannerRenderer
              {...data}
              {...props}
              index={index}
              isActive={activeRealIndex === index}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {showNavigation && (
        <>
          <IconButton
            position="absolute"
            aria-label="prevElement"
            left="1rem"
            ref={(el) => setPrevEl(el as HTMLButtonElement)}
            icon={<IoIosArrowBack />}
            {...defaultIconButtonProps}
          />
          <IconButton
            aria-label="nextElement"
            ref={(el) => setNextEl(el as HTMLButtonElement)}
            position="absolute"
            right="1rem"
            icon={<IoIosArrowForward />}
            {...defaultIconButtonProps}
          />
        </>
      )}
    </Box>
  );
}

export default BannerCarousel;
